<script>
// `artist` is declared here rather than in setup because vue-meta evaluates page() through a
// watcher that cannot see <script setup> bindings, and the artist is fetched async so page()
// cannot re-derive it from $route or $store. Setup writes it once the fetch lands.
export default {
    data() {
        return {
            artist: {},
        }
    },

    page() {
        const title = this.artist.title ? this.artist.title : 'Artist'
        const genre = this.artist && this.artist.genre && this.artist.genre.length ? this.artist.genre[0] : ''
        let description
        if (this.artist && this.artist.biography) {
            description = this.artist.biography.length > 200
                ? this.artist.biography.substring(0, 200) + '...'
                : this.artist.biography
        } else {
            description = `Listen to ${title} on FRISKY${genre ? ' • ' + genre : ''}`
        }
        const image = (this.artist && this.artist.hero && this.artist.hero.url)
            || (this.artist && this.artist.photo && this.artist.photo.url)
            || `${import.meta.env.VITE_CLIENT_URL}/frisky-social-share-image.png`
        const canonicalUrl = `https://frisky.fm${this.$route.path}`
        return {
            title,
            link: [{ rel: 'canonical', vmid: 'canonical', href: canonicalUrl }],
            meta: [
                { name: 'title', vmid: 'title', content: title },
                { name: 'description', vmid: 'description', content: description },

                { property: 'og:title', vmid: 'og:title', content: title },
                { property: 'og:description', vmid: 'og:description', content: description },
                { property: 'og:image', vmid: 'og:image', content: image },
                { property: 'og:type', vmid: 'og:type', content: 'music.song' },
                { property: 'og:url', vmid: 'og:url', content: canonicalUrl },
                { property: 'og:site_name', vmid: 'og:site_name', content: 'FRISKY' },

                { name: 'twitter:card', vmid: 'twitter:card', content: 'summary_large_image' },
                { name: 'twitter:title', vmid: 'twitter:title', content: title },
                { name: 'twitter:description', vmid: 'twitter:description', content: description },
                { name: 'twitter:image', vmid: 'twitter:image', content: image },
                { name: 'twitter:url', vmid: 'twitter:url', content: canonicalUrl },
            ],
        }
    },
}
</script>

<script setup>
import { computed, getCurrentInstance, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router/composables'
import DataLayer from '@services/data-layer/index'
import Layout from '@layouts/main-layout'

import Overview from '@views/artist/artist.overview'
import About from '@views/artist/artist.about'

// `<component :is>` resolves a STRING against registered components, never against
// <script setup> bindings — the routes name the tab, so the name is mapped back here.
const MAIN_COMPONENTS = { Overview, About }

const props = defineProps({
    mainComponent: {
        type: String,
        default: 'Overview',
    },
})

const instance = getCurrentInstance()
const route = useRoute()

// Setup's own copy: a binding called `artist` would shadow the options data property the template
// and page() rely on.
const loadedArtist = ref({})

const latestMixes = ref(Array.from(Array(6), (_mix, id) => ({ id, genre: [] })))
const popularMixes = ref(Array.from(Array(6), (_mix, id) => ({ id, genre: [] })))

const mainView = computed(() => MAIN_COMPONENTS[props.mainComponent])

const latestMix = computed(() => latestMixes.value[0])

const heroArtist = computed(() => ({ ...loadedArtist.value, latestMix: latestMix.value }))

const tabs = computed(() => {
    const artistUrl = loadedArtist.value.url
    // Route params are strings by default; matching the active tab depends on it.
    const id = String(loadedArtist.value.id)
    return [
        { to: { name: 'artist', params: { artistUrl, id } }, label: 'Overview' },
        { to: { name: 'artist/about', params: { artistUrl, id } }, label: 'About' },
    ]
})

onMounted(() => {
    Promise.all([
        DataLayer.artists.getById(route.params.id),
        DataLayer.mixes.getPopularByArtist(route.params.id),
        DataLayer.mixes.getLatestByArtist(route.params.id),
    ]).then(([artistResponse, popular, latest]) => {
        loadedArtist.value = artistResponse.body
        // page() reads this off the instance; `$data` is initialised by the time onMounted runs.
        instance.proxy.artist = artistResponse.body
        popularMixes.value = popular.body
        latestMixes.value = latest.body
    })
})
</script>

<template>
    <Layout>
        <BaseHero
            :resource="heroArtist"
            resource-type="artist"
        />
        <BaseTabPanel
            :tabs="tabs"
            class="tabs"
            is-unpadded
            address-navigation
        />
        <BaseContent>
            <component
                :is="mainView"
                :artist="artist"
                :popular-mixes="popularMixes"
                :latest-mixes="latestMixes"
            />
        </BaseContent>
    </Layout>
</template>

<style lang="scss" scoped>
@import '@design';

.tabs {
    @include tablet {
        padding-bottom: $s-xxl;
    }
}
</style>
